import React from 'react';
import { Database, TrendingUp, TrendingDown, AlertTriangle, RefreshCw, Inbox } from 'lucide-react';
import { DESIGN_SYSTEM, STATUS_ALIAS } from '../../theme/designSystem';
import { useI18n } from '../../i18n/useI18n';

function resolveStatus(statusKey, status) {
  const raw = (statusKey || status || 'INACTIF').toString();
  const upper = raw.toUpperCase();
  if (DESIGN_SYSTEM.status[upper]) return DESIGN_SYSTEM.status[upper];
  const alias = STATUS_ALIAS[raw.toLowerCase()];
  return DESIGN_SYSTEM.status[alias] || DESIGN_SYSTEM.status.INACTIF;
}

export function StatusBadge({ statusKey, status, customLabel, text }) {
  const config = resolveStatus(statusKey, status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${config.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {customLabel || text || config.label}
    </span>
  );
}

export function DemoDataBanner({ workerName = 'Worker Admin' }) {
  const { t } = useI18n();
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-[11px] font-semibold text-amber-800">
      <Database size={13} className="text-amber-600 shrink-0" />
      <span>{t('common.demo')} — {t('common.preparation')} {workerName}</span>
    </div>
  );
}

export function StatCard({ title, value, evolution, isPositive = true, context, icon: Icon, color = 'indigo', loading }) {
  const colorMap = {
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    purple: 'bg-violet-50 text-violet-600 border-violet-100',
    rose: 'bg-rose-50 text-rose-600 border-rose-100',
  };

  return (
    <div className="dsi-card dsi-card-hover p-5 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{title}</span>
        {Icon && (
          <div className={`p-2.5 rounded-xl border ${colorMap[color] || colorMap.indigo}`}>
            <Icon size={18} strokeWidth={2.2} />
          </div>
        )}
      </div>
      <div className="space-y-1">
        {loading ? (
          <div className="skeleton h-8 w-24" />
        ) : (
          <p className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
        )}
        <div className="flex items-center gap-2 text-xs font-medium">
          {evolution && (
            <span className={`inline-flex items-center gap-0.5 font-semibold ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
              {isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              {evolution}
            </span>
          )}
          {context && <span className="text-slate-400">{context}</span>}
        </div>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, workerName, demo, actions }) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
      <div>
        <h2 className="text-2xl sm:text-[28px] font-bold text-slate-900 tracking-tight">{title}</h2>
        {subtitle && <p className="text-slate-500 text-sm mt-1 font-medium">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {demo && <DemoDataBanner workerName={workerName} />}
        {actions}
      </div>
    </div>
  );
}

export function EmptyState({ title, message, onRetry }) {
  const { t } = useI18n();
  return (
    <div className="p-10 text-center dsi-card space-y-3">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto" style={{ background: 'var(--surface-secondary)', color: 'var(--text-muted)' }}>
        <Inbox size={24} />
      </div>
      <div className="max-w-sm mx-auto">
        <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{title || t('common.noData')}</h4>
        <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{message || t('common.empty')}</p>
      </div>
      {onRetry && (
        <button onClick={onRetry} className="dsi-btn-secondary text-xs">
          <RefreshCw size={14} /> {t('common.refresh')}
        </button>
      )}
    </div>
  );
}

export function WorkerErrorState({ workerName = "Worker Métier", onRetry }) {
  return (
    <div className="p-6 bg-rose-50/70 rounded-2xl border border-rose-200 text-center space-y-3">
      <div className="w-10 h-10 bg-rose-100 text-rose-600 rounded-xl flex items-center justify-center mx-auto">
        <AlertTriangle size={20} />
      </div>
      <div>
        <h4 className="text-xs font-semibold text-rose-900">Connexion au {workerName} indisponible</h4>
        <p className="text-[11px] text-rose-700 mt-0.5">Impossible d'établir le canal sécurisé. Vérifiez l'état du service.</p>
      </div>
      {onRetry && (
        <button onClick={onRetry} className="dsi-btn-danger text-xs">
          Réessayer la connexion
        </button>
      )}
    </div>
  );
}

export function PreparedModuleCard({ title, description, items = [] }) {
  return (
    <div className="dsi-card p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500 mt-1">{description}</p>
        </div>
        <StatusBadge statusKey="PREPARATION" />
      </div>
      {items.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          {items.map((item) => (
            <div key={item} className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-medium text-slate-500">
              {item}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function HorizontalBars({ rows }) {
  const max = Math.max(...rows.map((r) => r.value || 0), 1);
  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.label}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-medium text-slate-600">{row.label}</span>
            <span className="font-semibold text-slate-900">{row.value}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full rounded-full ${row.color || 'bg-indigo-500'}`}
              style={{ width: `${Math.round(((row.value || 0) / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function MiniSpark({ points = [], color = '#4f46e5' }) {
  if (!points.length) return null;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const w = 88;
  const h = 28;
  const path = points.map((p, i) => {
    const x = (i / Math.max(points.length - 1, 1)) * w;
    const y = h - ((p - min) / Math.max(max - min, 1)) * h;
    return `${i === 0 ? 'M' : 'L'}${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function DonutChart({ segments, centerLabel, centerValue }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0) || 1;
  let acc = 0;
  const r = 36;
  const c = 2 * Math.PI * r;
  return (
    <div className="flex items-center gap-5">
      <div className="relative w-28 h-28">
        <svg viewBox="0 0 100 100" className="-rotate-90 w-full h-full">
          <circle cx="50" cy="50" r={r} fill="none" stroke="#e2e8f0" strokeWidth="10" />
          {segments.map((seg) => {
            const len = ((seg.value || 0) / total) * c;
            const dash = `${len} ${c - len}`;
            const offset = -acc;
            acc += len;
            return (
              <circle
                key={seg.label}
                cx="50"
                cy="50"
                r={r}
                fill="none"
                stroke={seg.color}
                strokeWidth="10"
                strokeDasharray={dash}
                strokeDashoffset={offset}
                strokeLinecap="round"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-slate-900 leading-none">{centerValue}</span>
          <span className="text-[10px] text-slate-500 mt-1">{centerLabel}</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center gap-2 text-xs text-slate-600">
            <span className="w-2 h-2 rounded-full" style={{ background: seg.color }} />
            <span>{seg.label}</span>
            <span className="font-semibold text-slate-900">{seg.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AreaChart({ series }) {
  const values = series?.length ? series : [0];
  const max = Math.max(...values, 1);
  const w = 560;
  const h = 180;
  const step = w / Math.max(values.length - 1, 1);
  const pts = values.map((v, i) => `${i * step},${h - (v / max) * (h - 16) - 8}`);
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p}`).join(' ');
  const area = `${line} L${(values.length - 1) * step},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-44">
      <defs>
        <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#areaFill)" />
      <path d={line} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
